import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import { useSurveyParams } from '../context/SurveyParamsContext'
import 'survey-core/i18n/traditional-chinese'
import {
  convertVisibleIf,
  convertQuestionMappings,
  convertTriggers,
  q,
  v,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  const { sfdSamplerId, sfdPId } = useSurveyParams()
  // 需要登入嗎
  const requiresLogin = {
    login: false,
    title: '紙上的編號',
    placeholder: 'T000',
    alert: '編號有誤，請重新輸入，或請工作人員協助',
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = null
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)
  // 防止閃閃
  useEffect(() => {
    const timeout = setTimeout(() => {
      const isMobile = window.innerWidth <= 576
      if (!isMobile) return

      const observer = new MutationObserver((mutationsList) => {
        for (let mutation of mutationsList) {
          if (mutation.type === 'childList') {
            const rootEl = document.querySelector('.sd-root-modern')
            if (
              rootEl &&
              !rootEl.classList.contains('sd-root-modern--mobile')
            ) {
              rootEl.classList.add('sd-root-modern--mobile')
            }
          }
        }
      })

      observer.observe(document.getElementById('root'), {
        childList: true,
        subtree: true,
      })

      return () => observer.disconnect()
    }, 100)

    return () => clearTimeout(timeout)
  }, [])

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // 篩出（中止）條件 → 問卷結束
  // 題號以目前 API 回傳 JSON 的實際題序為準。
  const triggersConditionsInit = convertTriggers([
    {
      type: 'complete',
      expression:
        '({q1} = 1) or ({q3} = 3) or ({q4} = 1) or ({q4} = 2) or ({q5} = 2)',
    },
  ])
  const visibleIfConditionsInit = convertVisibleIf({
    // A2 沒有工作時隱藏 A3、A4
    q4: '{q3} <> 3',
    q5: '{q3} <> 3',

    // B3 婚姻 = 已婚或同居才顯示配偶工作、配偶育嬰相關題目
    q9: '{q8} = 2',
    q18: '{q8} = 2',
    q19: '{q8} = 2 and {q18} = 1',

    // C4 固定班別才顯示 C5 工作時間；輪班/不固定班別只跳過 C5
    q14: '{q13} = 1',
    // 「過去有沒有申請過以月為單位的育嬰留職停薪經驗」有申請才顯示申請月數
    q17: '{q16} = 1',

    // E1 日申請育嬰留停：有申請顯示 E2-E10；知道但未申請顯示 E11
    q26: '{q25} = 1',
    q27: '{q25} = 1',
    q28: '{q25} = 1',
    q29: '{q25} = 1',
    q30: '{q25} = 1',
    q31: '{q25} = 1',
    q32: '{q25} = 1',
    q33: '{q25} = 1',
    q34: '{q25} = 1',
    q35: '{q25} = 2',

    // F1 小時家庭照顧假：有申請顯示 F2-F5；知道但未申請顯示 F6
    q37: '{q36} = 1',
    q38: '{q36} = 1',
    q39: '{q36} = 1',
    q40: '{q36} = 1',
    q41: '{q36} = 2',

    // G6 認同顯示 G7；不認同顯示 G8
    q48: '{q47} = 1 or {q47} = 2',
    q49: '{q47} = 3',
  })
  const notRequired = null
  const mappedRateChoice = null
  const pageBreakQuestions = null
  const questionMappings = convertQuestionMappings({})

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
    sfdSamplerId,
    sfdPId,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    const model = new Model(convertedSurvey)
    ;['q20', 'q21'].forEach((questionName) => {
      const question = model.getQuestionByName(questionName)
      if (question) question.maxSelectedChoices = 3
    })
    return model
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
    sfdSamplerId,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  const endSurveyMsg = (survey) => {
    const isTerminated =
      survey.getValue(q(1)) === v(1) ||
      survey.getValue(q(3)) === v(3) ||
      survey.getValue(q(4)) === v(1) ||
      survey.getValue(q(4)) === v(2) ||
      survey.getValue(q(5)) === v(2)

    if (isTerminated) {
      setCompletionMessage(
        `<div>非常感謝您協助填答～<br><br>若有任何問題，歡迎聯繫：<br>聯絡人：張先生<br>電話：02-23315133#816<br>手機：0930098480<br>Line ID：zxcvbnm920606</div>`
      )
      return
    }

    setCompletionMessage(
      `<div>非常感謝您協助填答～<br><br>由於後續需要進行約5分鐘的線上回饋訪問，麻煩您從下方網址下載紙本問卷。<br><a href="https://drive.google.com/file/d/1CudVUuATsgLDziFhWC2FjuDkJR6reJJQ/view?usp=sharing" target="_blank">https://drive.google.com/file/d/1CudVUuATsgLDziFhWC2FjuDkJR6reJJQ/view?usp=sharing</a><br><br>若有任何問題，歡迎聯繫：<br>聯絡人：張先生<br>電話：02-23315133#816<br>手機：0930098480<br>Line ID：zxcvbnm920606</div>`
    )
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    enableProgressSaving:
      requiresLogin.login || webAuthType > 0 || !!sfdSamplerId,
    webAuthType,
    sfdSamplerId,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
